import React, { useState } from 'react';
import {
  Button,
  Space,
  Select,
  Input,
  InputNumber,
  Modal,
  Form,
  message,
} from 'antd';
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  FolderOutlined,
} from '@ant-design/icons';
import type {
  MenuCategory,
  MenuDish,
  DishStatus,
  StockStatus,
} from '../../../types/menu';

export interface BulkActionBarProps {
  selectedIds: string[];
  dishes: MenuDish[];
  categories: MenuCategory[];
  onBulkPublish: (ids: string[], adminNote: string) => Promise<void>;
  onBulkHide: (ids: string[], adminNote: string) => Promise<void>;
  onBulkStock: (
    ids: string[],
    stockStatus: StockStatus,
    adminNote: string
  ) => Promise<void>;
  onBulkPrice: (
    ids: string[],
    delta?: number,
    percent?: number,
    adminNote?: string
  ) => Promise<void>;
  onBulkMoveCategory: (
    ids: string[],
    categoryId: string | null,
    adminNote: string
  ) => Promise<void>;
  onClearSelection: () => void;
  onConfirmWithNote: (
    action: string,
    fn: (note: string) => Promise<void>
  ) => void;
}

export const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedIds,
  dishes,
  categories,
  onBulkPublish,
  onBulkHide,
  onBulkStock,
  onBulkPrice,
  onBulkMoveCategory,
  onClearSelection,
  onConfirmWithNote,
}) => {
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [formPrice] = Form.useForm();
  const [formMove] = Form.useForm();

  const count = selectedIds.length;
  if (count === 0) return null;

  return (
    <div className="merchant-menu-bulk-bar">
      <div className="merchant-menu-bulk-info">
        <span className="merchant-menu-bulk-count">
          {count} dish{count !== 1 ? 'es' : ''} selected
        </span>
      </div>
      <Space wrap size="small">
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={() =>
            onConfirmWithNote('Publish', note =>
              onBulkPublish(selectedIds, note)
            )
          }
        >
          Publish
        </Button>
        <Button
          size="small"
          icon={<EyeInvisibleOutlined />}
          onClick={() =>
            onConfirmWithNote('Hide', note => onBulkHide(selectedIds, note))
          }
        >
          Hide
        </Button>
        <Button
          size="small"
          icon={<CheckCircleOutlined />}
          onClick={() =>
            onConfirmWithNote('Set In stock', note =>
              onBulkStock(selectedIds, 'IN_STOCK', note)
            )
          }
        >
          In stock
        </Button>
        <Button
          size="small"
          icon={<CloseCircleOutlined />}
          onClick={() =>
            onConfirmWithNote('Set Out of stock', note =>
              onBulkStock(selectedIds, 'OUT_OF_STOCK', note)
            )
          }
        >
          Out of stock
        </Button>
        <Button
          size="small"
          icon={<DollarOutlined />}
          onClick={() => setPriceModalOpen(true)}
        >
          Change price
        </Button>
        <Button
          size="small"
          icon={<FolderOutlined />}
          onClick={() => setMoveModalOpen(true)}
        >
          Move category
        </Button>
        <Button size="small" type="link" onClick={onClearSelection}>
          Clear selection
        </Button>
      </Space>

      <Modal
        title="Bulk change price"
        open={priceModalOpen}
        onCancel={() => {
          setPriceModalOpen(false);
          formPrice.resetFields();
        }}
        onOk={() => {
          formPrice.validateFields().then(values => {
            const { mode, fixedAmount, percent, adminNote } = values;
            const note = (adminNote ?? '').trim();
            if (!note) {
              message.error('Admin note is required');
              return;
            }
            if (mode === 'fixed' && fixedAmount != null) {
              onBulkPrice(selectedIds, Number(fixedAmount), undefined, note);
            } else if (mode === 'percent' && percent != null) {
              onBulkPrice(selectedIds, undefined, Number(percent), note);
            }
            setPriceModalOpen(false);
            formPrice.resetFields();
          });
        }}
        destroyOnClose
      >
        <Form
          form={formPrice}
          layout="vertical"
          initialValues={{ mode: 'fixed' }}
        >
          <Form.Item name="mode" label="Change type">
            <Select
              options={[
                { value: 'fixed', label: 'Fixed amount (add/subtract)' },
                { value: 'percent', label: 'Percentage' },
              ]}
            />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.mode !== curr.mode}
          >
            {({ getFieldValue }) =>
              getFieldValue('mode') === 'fixed' ? (
                <Form.Item
                  name="fixedAmount"
                  label="Amount (VND)"
                  rules={[{ required: true }]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="e.g. -5000"
                  />
                </Form.Item>
              ) : (
                <Form.Item
                  name="percent"
                  label="Percent (%)"
                  rules={[{ required: true }]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={-100}
                    max={100}
                    placeholder="e.g. 10"
                  />
                </Form.Item>
              )
            }
          </Form.Item>
          <Form.Item
            name="adminNote"
            label="Ghi chú quản trị viên (bắt buộc)"
            rules={[
              { required: true, message: 'Required' },
              { min: 3, message: 'At least 3 characters' },
            ]}
          >
            <Input.TextArea rows={2} placeholder="Reason for change" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Bulk move category"
        open={moveModalOpen}
        onCancel={() => {
          setMoveModalOpen(false);
          formMove.resetFields();
        }}
        onOk={() => {
          formMove.validateFields().then(values => {
            const { categoryId, adminNote } = values;
            const note = (adminNote ?? '').trim();
            if (!note) {
              message.error('Admin note is required');
              return;
            }
            onBulkMoveCategory(
              selectedIds,
              categoryId === '__none__' ? null : categoryId,
              note
            );
            setMoveModalOpen(false);
            formMove.resetFields();
          });
        }}
        destroyOnClose
      >
        <Form form={formMove} layout="vertical">
          <Form.Item
            name="categoryId"
            label="Target category"
            rules={[{ required: true }]}
          >
            <Select
              placeholder="Select category"
              options={[
                { value: '__none__', label: 'Uncategorized' },
                ...categories.map(c => ({
                  value: c.id,
                  label: `${c.name} (${c.dishCount})`,
                })),
              ]}
            />
          </Form.Item>
          <Form.Item
            name="adminNote"
            label="Ghi chú quản trị viên (bắt buộc)"
            rules={[
              { required: true, message: 'Required' },
              { min: 3, message: 'At least 3 characters' },
            ]}
          >
            <Input.TextArea rows={2} placeholder="Reason for change" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
